import React from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ArrowUpDown, ChevronDown } from "lucide-react";
import { cn } from "@/components/utils/utils";

export type SortField = "price" | "rating" | "name" | "distance";
export type SortDir = "asc" | "desc";

type SortMode = "price-asc" | "price-desc" | "rating-desc" | "name-asc" | "distance-asc";

interface SortResultsButtonProps {
  sortField: SortField;
  sortDir: SortDir;
  onSortChange: (field: SortField, dir: SortDir) => void;
  disabled?: boolean;
}

const SORT_OPTIONS: { mode: SortMode; label: string; field: SortField; dir: SortDir }[] = [
  { mode: "price-asc",     label: "Price (lowest first)",    field: "price",    dir: "asc"  },
  { mode: "price-desc",    label: "Price (highest first)",   field: "price",    dir: "desc" },
  { mode: "rating-desc",   label: "Rating (highest first)",  field: "rating",   dir: "desc" },
  { mode: "name-asc",      label: "Name (A–Z)",              field: "name",     dir: "asc"  },
  { mode: "distance-asc",  label: "Distance (nearest first)", field: "distance", dir: "asc" },
];

function resolveMode(field: SortField, dir: SortDir): SortMode {
  const found = SORT_OPTIONS.find((o) => o.field === field && o.dir === dir);
  return found ? found.mode : "price-asc";
}

export const SortResultsButton: React.FC<SortResultsButtonProps> = ({
  sortField,
  sortDir,
  onSortChange,
  disabled = false,
}) => {
  const activeMode = resolveMode(sortField, sortDir);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="flex items-center gap-1 sm:gap-2"
          disabled={disabled}
        >
          <ArrowUpDown className="h-4 w-4 flex-shrink-0" />
          <span className="hidden sm:inline">
            {SORT_OPTIONS.find((o) => o.mode === activeMode)?.label ?? "Sort"}
          </span>
          <ChevronDown className="h-4 w-4 flex-shrink-0" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {SORT_OPTIONS.map(({ mode, label, field, dir }) => (
          <DropdownMenuItem
            key={mode}
            onClick={() => onSortChange(field, dir)}
            className={cn(activeMode === mode && "bg-accent")}
          >
            {label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
